import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRooms } from '../../context/RoomContext';
import { format, formatISO } from 'date-fns';
import { 
  Calendar, 
  User, 
  Mail, 
  Phone, 
  CreditCard, 
  DollarSign,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Clock,
  Check,
  X,
  AlertTriangle,
  Trash2,
  Tag,
  Percent
} from 'lucide-react';

const BookingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { 
    getBookingById, 
    getRoomById, 
    addPayment, 
    updateBookingStatus, 
    deleteBooking,
    applyDiscount,
    removeDiscount,
    updateBookingNotes
  } = useRooms();
  
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | 'bank_transfer'>('cash');
  const [paymentDate, setPaymentDate] = useState<string>(
    formatISO(new Date()).slice(0, 16) // Format: YYYY-MM-DDTHH:mm
  );
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Notes state
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [userNotes, setUserNotes] = useState<string>('');
  const [isUpdatingNotes, setIsUpdatingNotes] = useState(false);
  
  // Discount state
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [discountValue, setDiscountValue] = useState<string>('');
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  
  const booking = getBookingById(id || '');
  const room = booking ? getRoomById(booking.roomId) : undefined;
  
  // Initialize notes when booking is loaded
  React.useEffect(() => {
    if (booking) {
      setAdminNotes(booking.adminNotes || '');
      setUserNotes(booking.userNotes || '');
    }
  }, [booking]);
  
  if (!booking || !room) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900">Booking not found</h2>
        <button
          onClick={() => navigate('/admin/bookings')}
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
        >
          Back to Bookings
        </button>
      </div>
    );
  }

  const payments = Array.isArray(booking.payments) ? booking.payments : [];
  const totalPaid = payments.reduce((sum, payment) => sum + (payment?.amount || 0), 0);
  const remainingAmount = booking.totalPrice - totalPaid;
  const isFullyPaid = remainingAmount <= 0;
  
  // Discount calculations
  const originalPrice = booking.originalPrice || booking.totalPrice;
  const hasDiscount = booking.discountType && booking.discountType !== 'none' && (booking.discountAmount || 0) > 0;

  const handleAddPayment = async () => {
    try {
      const amount = parseFloat(paymentAmount);
      if (isNaN(amount) || amount <= 0) {
        setError('Please enter a valid payment amount');
        return;
      }

      if (amount > remainingAmount) {
        setError('Payment amount cannot exceed the remaining balance');
        return;
      }

      // Validate payment date
      if (!paymentDate) {
        setError('Please select a payment date and time');
        return;
      }
      
      await addPayment(booking.id, {
        amount,
        date: new Date(paymentDate).toISOString(),
        method: paymentMethod,
      });

      setSuccess('Payment added successfully');
      setPaymentAmount('');
      setPaymentDate(formatISO(new Date()).slice(0, 16));
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error adding payment:', error);
      setError('Failed to add payment');
    }
  };

  const handleStatusChange = async (newStatus: 'pending' | 'confirmed' | 'cancelled') => {
    try {
      setIsUpdatingStatus(true);
      await updateBookingStatus(booking.id, newStatus);
      setSuccess(`Booking status updated to ${newStatus}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Failed to update booking status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDeleteBooking = async () => {
    try {
      setIsDeleting(true);
      await deleteBooking(booking.id);
      setSuccess('Booking deleted successfully');
      setTimeout(() => {
        navigate('/admin/bookings');
      }, 1000);
    } catch (error) {
      console.error('Error deleting booking:', error);
      setError('Failed to delete booking');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  const handleUpdateNotes = async () => {
    try {
      setIsUpdatingNotes(true);
      await updateBookingNotes(booking.id, adminNotes, userNotes);
      setSuccess('Notes updated successfully');
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating notes:', error);
      setError('Failed to update notes');
    } finally {
      setIsUpdatingNotes(false);
    }
  };

  const handleApplyDiscount = async () => {
    try {
      const value = parseFloat(discountValue);
      if (isNaN(value) || value <= 0) {
        setError('Please enter a valid discount value');
        return;
      }

      if (discountType === 'percentage' && value > 100) {
        setError('Percentage discount cannot exceed 100%');
        return;
      }

      if (discountType === 'fixed' && value > originalPrice) {
        setError('Fixed discount cannot exceed the original price');
        return;
      }

      setIsApplyingDiscount(true);
      await applyDiscount(booking.id, discountType, value);
      setSuccess('Discount applied successfully');
      setShowDiscountForm(false);
      setDiscountValue('');
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error applying discount:', error);
      setError('Failed to apply discount');
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  const handleRemoveDiscount = async () => {
    try {
      setIsApplyingDiscount(true);
      await removeDiscount(booking.id);
      setSuccess('Discount removed successfully');
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error removing discount:', error);
      setError('Failed to remove discount');
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <Check className="h-5 w-5 text-green-500" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'cancelled':
        return <X className="h-5 w-5 text-red-500" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/bookings')}
          className="inline-flex items-center text-indigo-600 hover:text-indigo-900"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          Back to Bookings
        </button>
      </div>

      {success && (
        <div className="mb-4 bg-green-50 p-4 rounded-lg flex items-center text-sm text-green-700">
          <CheckCircle className="h-5 w-5 mr-2 text-green-400" />
          {success}
        </div>
      )}

      {error && (
        <div className="mb-4 bg-red-50 p-4 rounded-lg flex items-center text-sm text-red-700">
          <AlertCircle className="h-5 w-5 mr-2 text-red-400" />
          {error}
        </div>
      )}

      {/* Booking Details Section */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                Booking Details
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Booking #IFA-{booking.id.substring(0, 8)}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <button
                  onClick={() => setIsUpdatingStatus(!isUpdatingStatus)}
                  className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium ${getStatusColor(booking.status)}`}
                  disabled={isUpdatingStatus}
                >
                  {getStatusIcon(booking.status)}
                  <span className="ml-2 capitalize">{booking.status}</span>
                </button>
                {isUpdatingStatus && (
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1" role="menu">
                      <button
                        onClick={() => handleStatusChange('pending')}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${booking.status === 'pending' ? 'bg-gray-50' : ''}`}
                      >
                        <Clock className="inline-block h-4 w-4 mr-2 text-yellow-500" />
                        Pending
                      </button>
                      <button
                        onClick={() => handleStatusChange('confirmed')}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${booking.status === 'confirmed' ? 'bg-gray-50' : ''}`}
                      >
                        <Check className="inline-block h-4 w-4 mr-2 text-green-500" />
                        Confirmed
                      </button>
                      <button
                        onClick={() => handleStatusChange('cancelled')}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${booking.status === 'cancelled' ? 'bg-gray-50' : ''}`}
                      >
                        <X className="inline-block h-4 w-4 mr-2 text-red-500" />
                        Cancelled
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowDeleteConfirmation(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Booking
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 py-5 sm:px-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <User className="h-5 w-5 mr-2" />
                Guest Name
              </dt>
              <dd className="mt-1 text-sm text-gray-900">{booking.userName}</dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <Mail className="h-5 w-5 mr-2" />
                Email
              </dt>
              <dd className="mt-1 text-sm text-gray-900">{booking.userEmail}</dd>
            </div>

            {booking.userPhone && (
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <Phone className="h-5 w-5 mr-2" />
                  Phone Number
                </dt>
                <dd className="mt-1 text-sm text-gray-900">{booking.userPhone}</dd>
              </div>
            )}

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <Calendar className="h-5 w-5 mr-2" />
                Check-in
              </dt>
              <dd className="mt-1 text-sm text-gray-900">{booking.checkIn}</dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <Calendar className="h-5 w-5 mr-2" />
                Check-out
              </dt>
              <dd className="mt-1 text-sm text-gray-900">{booking.checkOut}</dd>
            </div>

            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">Room Details</dt>
              <dd className="mt-1 text-sm text-gray-900">
                <div className="flex items-start">
                  <img
                    src={room.image_url}
                    alt={room.name}
                    className="h-24 w-24 object-cover rounded-md"
                  />
                  <div className="ml-4">
                    <h4 className="font-bold">{room.name}</h4>
                    <p className="text-gray-500">{room.description}</p>
                  </div>
                </div>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Notes Section */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">Booking Notes</h3>
        </div>

        <div className="px-4 py-5 sm:px-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Notes (Internal)
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={4}
                className="form-input min-h-[100px]"
                placeholder="Internal notes for staff use only..."
              />
              <p className="mt-1 text-xs text-gray-500">
                These notes are only visible to admin staff and will not be shared with guests.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Guest Notes/Special Requests
              </label>
              <textarea
                value={userNotes}
                onChange={(e) => setUserNotes(e.target.value)}
                rows={4}
                className="form-input min-h-[100px]"
                placeholder="Guest special requests, dietary requirements, preferences..."
              />
              <p className="mt-1 text-xs text-gray-500">
                Notes and special requests from the guest.
              </p>
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={handleUpdateNotes}
                disabled={isUpdatingNotes}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdatingNotes ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </>
                ) : (
                  'Update Notes'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Discount Section */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-900">Discount Management</h3>
            {!hasDiscount && !showDiscountForm && (
              <button
                onClick={() => setShowDiscountForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <Tag className="h-4 w-4 mr-2" />
                Apply Discount
              </button>
            )}
          </div>
        </div>

        <div className="px-4 py-5 sm:px-6">
          {/* Current Discount Display */}
          {hasDiscount && (
            <div className="bg-green-50 p-4 rounded-lg mb-6">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="text-lg font-medium text-green-900 mb-2">Active Discount</h4>
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <Tag className="h-4 w-4 text-green-600 mr-2" />
                      <span className="text-green-700">
                        {booking.discountType === 'percentage' 
                          ? `${booking.discountValue}% discount`
                          : `$${booking.discountValue} fixed discount`
                        }
                      </span>
                    </div>
                    <div className="flex items-center text-sm">
                      <DollarSign className="h-4 w-4 text-green-600 mr-2" />
                      <span className="text-green-700">
                        Discount Amount: ${booking.discountAmount?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleRemoveDiscount}
                  disabled={isApplyingDiscount}
                  className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {isApplyingDiscount ? 'Removing...' : 'Remove Discount'}
                </button>
              </div>
            </div>
          )}

          {/* Discount Form */}
          {showDiscountForm && !hasDiscount && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Apply Discount</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Type
                  </label>
                  <select
                    value={discountType}
                    onChange={(e) => setDiscountType(e.target.value as 'percentage' | 'fixed')}
                    className="form-select h-[50px]"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Value
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      {discountType === 'percentage' ? (
                        <Percent className="h-5 w-5 text-gray-400" />
                      ) : (
                        <DollarSign className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    <input
                      type="number"
                      value={discountValue}
                      onChange={(e) => {
                        setDiscountValue(e.target.value);
                        setError('');
                      }}
                      className="form-input-with-icon h-[50px]"
                      placeholder={discountType === 'percentage' ? '10' : '50.00'}
                      min="0"
                      max={discountType === 'percentage' ? '100' : originalPrice.toString()}
                      step={discountType === 'percentage' ? '1' : '0.01'}
                    />
                  </div>
                </div>
                
                <div className="flex items-end">
                  <div className="flex space-x-2 w-full">
                    <button
                      onClick={handleApplyDiscount}
                      disabled={!discountValue || isApplyingDiscount}
                      className="flex-1 inline-flex justify-center items-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isApplyingDiscount ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Applying...
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Apply
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowDiscountForm(false);
                        setDiscountValue('');
                        setError('');
                      }}
                      className="flex-1 inline-flex justify-center items-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Discount Preview */}
              {discountValue && (
                <div className="mt-4 p-3 bg-blue-50 rounded-md">
                  <h5 className="text-sm font-medium text-blue-900 mb-2">Discount Preview</h5>
                  <div className="text-sm text-blue-700 space-y-1">
                    <div className="flex justify-between">
                      <span>Original Price:</span>
                      <span>${originalPrice.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Discount:</span>
                      <span>
                        {discountType === 'percentage' 
                          ? `-$${((originalPrice * parseFloat(discountValue)) / 100).toFixed(2)} (${discountValue}%)`
                          : `-$${Math.min(parseFloat(discountValue), originalPrice).toFixed(2)}`
                        }
                      </span>
                    </div>
                    <div className="flex justify-between font-medium border-t border-blue-200 pt-1">
                      <span>Final Price:</span>
                      <span>
                        ${discountType === 'percentage' 
                          ? Math.max(0, originalPrice - (originalPrice * parseFloat(discountValue)) / 100).toFixed(2)
                          : Math.max(0, originalPrice - Math.min(parseFloat(discountValue), originalPrice)).toFixed(2)
                        }
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Price Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-medium text-gray-900 mb-3">Price Summary</h4>
            <div className="space-y-2">
              {hasDiscount && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Original Price:</span>
                  <span className="text-gray-900">${originalPrice.toFixed(2)}</span>
                </div>
              )}
              {hasDiscount && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Discount Applied:</span>
                  <span className="text-red-600">-${(booking.discountAmount || 0).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-medium border-t border-gray-200 pt-2">
                <span className="text-gray-900">Final Price:</span>
                <span className="text-indigo-600">${booking.totalPrice.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Status Section */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">Payment Status</h3>
        </div>

        <div className="px-4 py-5 sm:px-6">
          <div className="bg-gray-50 p-6 rounded-lg mb-6">
            <div className="flex justify-between mb-4">
              <span className="font-medium">Total Amount:</span>
              <span className="font-bold">${booking.totalPrice}</span>
            </div>
            <div className="flex justify-between mb-4">
              <span className="font-medium">Paid Amount:</span>
              <span className="font-bold text-green-600">${totalPaid}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Remaining Amount:</span>
              <span className={`font-bold ${isFullyPaid ? 'text-green-600' : 'text-red-600'}`}>
                ${remainingAmount}
              </span>
            </div>
          </div>

          {payments.length > 0 && (
            <div className="mb-8">
              <h4 className="text-lg font-bold mb-4">Payment History</h4>
              <div className="space-y-3">
                {payments.map(payment => (
                  <div
                    key={payment.id}
                    className="flex justify-between items-center bg-gray-50 p-4 rounded-lg"
                  >
                    <div className="flex items-center">
                      <CreditCard className="h-5 w-5 mr-2 text-gray-400" />
                      <span className="capitalize font-medium">{payment.method}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="mr-4 text-gray-500">
                        {format(new Date(payment.date), 'MMM d, yyyy h:mm a')}
                      </span>
                      <span className="font-bold">${payment.amount}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isFullyPaid && booking.status !== 'cancelled' && (
            <div>
              <h4 className="text-lg font-bold mb-4">Add Payment</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                    Amount
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DollarSign className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      name="amount"
                      id="amount"
                      max={remainingAmount}
                      value={paymentAmount}
                      onChange={(e) => {
                        setPaymentAmount(e.target.value);
                        setError('');
                      }}
                      className="form-input-with-icon h-[50px]"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="method" className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Method
                  </label>
                  <select
                    id="method"
                    name="method"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value as any)}
                    className="form-select h-[50px]"
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="bank_transfer">Bank Transfer</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="paymentDate" className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Date & Time
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Clock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="datetime-local"
                      name="paymentDate"
                      id="paymentDate"
                      value={paymentDate}
                      onChange={(e) => {
                        setPaymentDate(e.target.value);
                        setError('');
                      }}
                      className="form-input-with-icon h-[50px]"
                      max={formatISO(new Date()).slice(0, 16)}
                    />
                  </div>
                </div>
              </div>
              <button
                onClick={handleAddPayment}
                className="mt-6 w-full inline-flex justify-center py-3 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Add Payment
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">Delete Booking</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-700 mb-3">
                  Are you sure you want to permanently delete this booking?
                </p>
                <div className="bg-red-50 p-3 rounded-md">
                  <p className="text-sm text-red-700">
                    <strong>Warning:</strong> This will permanently delete:
                  </p>
                  <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                    <li>The booking record</li>
                    <li>All associated payments</li>
                    <li>All booking notes</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirmation(false)}
                  disabled={isDeleting}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteBooking}
                  disabled={isDeleting}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Permanently
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingDetailPage;